import { compareHighlightTags, type HighlightTagColorSource } from '@/lib/highlightTagColors';
import { journalWriteNoteMatchesPractitioner } from '@/lib/journalWritePractitionerFilter';

/** A file marking the journal panel can filter. Same staff rule as notes. */
export type JournalWriteMarkingListItem = {
  created_by: string;
  author_name: string | null;
  quote: string;
  comment: string | null;
  filename: string;
  /** Markerings tags linked on the highlight. Missing means none. */
  tagIds?: readonly string[];
};

/**
 * Markings the open Markeringer list shows. The header count must use this
 * same result. Alle medarbejdere (`employees`) keeps every marking the viewer
 * can see. Alle behandlere hides markings by active staff who are not
 * practitioners. Search matches the quote, comment, file name, and author.
 * An empty tag list shows every remaining marking. One or more tags keep a
 * marking that has any of those tags.
 */
export function filterJournalWriteMarkings<T extends JournalWriteMarkingListItem>(
  rows: readonly T[],
  input: {
    practitionerId: string;
    practitionerIds: ReadonlySet<string>;
    activeMemberIds: ReadonlySet<string>;
    search: string;
    tagIds?: readonly string[];
  },
): T[] {
  const query = input.search.trim().toLocaleLowerCase('da');
  const selectedTags = new Set(input.tagIds ?? []);
  return rows.filter((row) => {
    if (
      !journalWriteNoteMatchesPractitioner({
        clinicianId: row.created_by,
        authorName: row.author_name,
        filterId: input.practitionerId,
        practitionerIds: input.practitionerIds,
        activeMemberIds: input.activeMemberIds,
      })
    ) {
      return false;
    }
    if (selectedTags.size > 0) {
      const linked = row.tagIds ?? [];
      if (!linked.some((id) => selectedTags.has(id))) return false;
    }
    if (!query) return true;
    const hay = [row.quote, row.comment ?? '', row.filename, row.author_name ?? '']
      .join('\n')
      .toLocaleLowerCase('da');
    return hay.includes(query);
  });
}

/**
 * Tags offered in the journal markeringer filter.
 * Active clinic tags stay, in Indstillinger order. An archived tag stays only
 * when a marking in the current list still uses it.
 */
export function journalMarkingFilterTags<T extends HighlightTagColorSource>(
  tags: readonly T[],
  usedTagIds: ReadonlySet<string>,
): T[] {
  return tags
    .filter((tag) => !tag.is_archived || usedTagIds.has(tag.id))
    .slice()
    .sort(compareHighlightTags);
}

/** How many markings in `rows` carry each tag. A tag on one marking counts once. */
export function journalMarkingTagCounts(
  rows: readonly Pick<JournalWriteMarkingListItem, 'tagIds'>[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    for (const id of new Set(row.tagIds ?? [])) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  return counts;
}

/** Short quote for the journal list. Guillemets match the file-preview list. */
export function markingQuotePreview(quote: string, max = 160): string {
  const compact = quote.replace(/\s+/g, ' ').trim();
  if (!compact) return '';
  const body = compact.length > max ? `${compact.slice(0, max - 1)}…` : compact;
  return `»${body}«`;
}
